import { Component, ElementRef, ViewChild } from '@angular/core';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { VelesMocaAssessmentService } from 'src/app/services/velesMocaAssessment.service';
import { IAddVelesMocaAssessment } from 'src/app/interfaces/Veles/IAddVelesMocaAssessment';
import { SwalToastService } from 'src/app/services/swal.service';
import { validateUpload } from 'src/app/guards/file.guard';

@Component({
  selector: 'app-moca-assessment-add',
  templateUrl: './moca-assessment-add.component.html',
  styleUrls: ['./moca-assessment-add.component.css'],
  standalone: false,
})
export class MocaAssessmentAddComponent {
  @ViewChild('fileInput', { static: false })
  fileInput?: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();

  fileObj?: File;

  isVisible = false;
  isLoading = false;

  mocaSubmitted = false;

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef;

  mocaForm: FormGroup = new FormGroup({});

  constructor(
    private readonly mocaService: VelesMocaAssessmentService,
    private readonly formBuilder: FormBuilder,
    private readonly swalToastService: SwalToastService
  ) {}

  ngOnInit() {
    this.mocaForm = this.formBuilder.group({
      scoreValue: [
        null,
        [
          Validators.required,
          Validators.min(0),
          Validators.max(30),
          Validators.pattern(/^\d+$/),
        ],
      ],
      fileValue: [null],
    });

    this.mocaService.mocaModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        this.isVisible = showModal;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this.modalRef.destroy();
  }

  handleCancel() {
    this.mocaForm.reset();
    this.fileObj = undefined;
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }

    this.mocaService.mocaModalSub.next(false);

    this.mocaSubmitted = false;

    this.isLoading = false;

    this.modalRef.destroy();
  }

  submitForm() {
    this.isLoading = true;
    this.mocaSubmitted = true;

    if (this.mocaForm.valid) {
      const mocaObj: IAddVelesMocaAssessment = {};

      mocaObj.score = this.mocaForm.value?.scoreValue;
      mocaObj.file = this.fileObj;

      this.mocaService.addMocaAssessment(mocaObj).subscribe({
        next: () => {
          this.swalToastService.toastSuccess(
            'MoCA assessment added successfully'
          );
          this.mocaService.getData();

          this.handleCancel();
        },
        error: () => {
          this.swalToastService.toastError(
            'Unable to add MoCA assessment'
          );
          this.handleCancel();
        },
      });
    } else {
      this.isLoading = false;
    }
  }

  async onFileSelected(event: any) {
    const file: File | null = event.target.files[0];
    if (!file) {
      this.fileObj = undefined;
      this.mocaForm.get('fileValue')?.setValue(undefined);
      this.mocaForm.get('fileValue')?.setErrors(null);
      return;
    }

    const result = await validateUpload(file);
    if (!result.ok) {
      this.swalToastService.toastError(result.error!);
      this.fileObj = undefined;
      this.mocaForm.get('fileValue')?.setValue(undefined);
      this.mocaForm.get('fileValue')?.setErrors(null);
      if (this.fileInput?.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
      return;
    }
    this.fileObj = file;
    this.mocaForm.get('fileValue')?.setValue(file.name);
    this.mocaForm.get('fileValue')?.setErrors(null);
  }
}
